/**
 * Analytics bond setup + auto-tracking — FIRST-PARTY page views.
 *
 * The privacy policy promises no third-party trackers, so the analytics
 * provider wired here never leaves molecule.dev: each route change becomes one
 * `page_view` event POSTed to our own `POST /analytics/events`, carrying only
 *
 *   path      — the route (the server strips the query string and masks ids)
 *   ref       — the acquisition channel from `?ref=<channel>` on a link we
 *               posted (`hn`, `reddit-lovable`, `yt-build1`, …), remembered in
 *               localStorage (`mol_ref`, {@link REF_TTL_MS}) so it survives the
 *               SPA session and reaches signup (see Signup.tsx)
 *   referrer  — `document.referrer` reduced to origin + path, sent once per tab
 *               session (it describes the FIRST navigation only; on later SPA
 *               route changes it would mislabel internal moves as referred)
 *   sid       — a random per-tab nonce (`mol_sid`, sessionStorage) so the
 *               scorecard can count visitors as distinct SESSIONS without the
 *               server storing or hashing an IP
 *
 * This is what answers "where did they come from?" (docs/marketing-plan.md §3).
 * It runs for anonymous visitors — the landing page is the whole point — so
 * the request is a bare `fetch` with the session cookie, not the app http
 * client: that client's error interceptor surfaces 429/5xx to the user as a
 * notification, and a dropped page view must never be visible.
 *
 * Every other auto-tracked signal (auth events, http errors) stays a no-op on
 * this provider; the server already emits the events those would duplicate.
 */

import type { AnalyticsProvider } from '@molecule/app-analytics'
import { setProvider, setupAutoTracking } from '@molecule/app-analytics'
import { getClient } from '@molecule/app-http'
import { getRouter } from '@molecule/app-routing'

import { API_BASE_URL } from '../config.js'
import { authClient } from './auth-default.js'

/** localStorage key holding the remembered acquisition channel. */
export const REF_STORAGE_KEY = 'mol_ref'

/** The query parameter every link we post carries (`?ref=hn`). */
export const REF_QUERY_PARAM = 'ref'

/** How long a captured ref is remembered — long enough to reach a later signup. */
export const REF_TTL_MS = 90 * 24 * 60 * 60 * 1000

/** sessionStorage key holding the per-tab session nonce. */
const SESSION_ID_KEY = 'mol_sid'

/** The event name the server records route changes under. */
export const PAGE_VIEW_EVENT = 'page_view'

/** Longest channel we ever post — matches the server's `MAX_REF_LENGTH`. */
const MAX_REF_LENGTH = 64

/** The remembered ref, as stored. */
interface StoredRef {
  ref: string
  expiresAt: number
}

/**
 * Normalize a channel the same way the server does (`analytics/page-view.ts`
 * `normalizeRef`): trimmed, lower-cased, `[a-z0-9._-]`, at most 64 chars.
 * Anything else is not a link we posted and is dropped.
 *
 * @param value - The raw `ref` value.
 * @returns The canonical channel, or `null`.
 */
export function normalizeRef(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null
  const ref = value.trim().toLowerCase()
  if (ref.length === 0 || ref.length > MAX_REF_LENGTH) return null
  return /^[a-z0-9][a-z0-9._-]*$/.test(ref) ? ref : null
}

/**
 * Remember the `?ref=` a page load carried. Last touch wins: a visitor who
 * follows a NEW link we posted is a new campaign touch, and that is the link
 * the eventual signup is attributed to.
 *
 * @param search - The location's query string (defaults to the window's).
 * @param now - The current time (ms), injectable for tests.
 * @returns The captured ref, or `null` when the URL carried none.
 */
export function captureRef(
  search: string = window.location.search,
  now = Date.now(),
): string | null {
  const ref = normalizeRef(new URLSearchParams(search).get(REF_QUERY_PARAM))
  if (!ref) return null
  const stored: StoredRef = { ref, expiresAt: now + REF_TTL_MS }
  try {
    localStorage.setItem(REF_STORAGE_KEY, JSON.stringify(stored))
  } catch (_error) {
    // Storage unavailable (private mode, quota) — the ref cannot be remembered,
    // so neither later page views nor signup will carry it.
  }
  return ref
}

/**
 * The remembered acquisition channel, if one was captured and has not expired.
 * An expired entry is removed on read.
 *
 * @param now - The current time (ms), injectable for tests.
 * @returns The channel, or `null`.
 */
export function getStoredRef(now = Date.now()): string | null {
  let raw: string | null
  try {
    raw = localStorage.getItem(REF_STORAGE_KEY)
  } catch (_error) {
    // Storage unreachable — nothing remembered.
    return null
  }
  if (!raw) return null
  let parsed: Partial<StoredRef>
  try {
    parsed = JSON.parse(raw) as Partial<StoredRef>
  } catch (_error) {
    // Corrupt entry — treated as absent and removed below.
    parsed = {}
  }
  const ref = normalizeRef(parsed.ref)
  if (!ref || typeof parsed.expiresAt !== 'number' || parsed.expiresAt <= now) {
    try {
      localStorage.removeItem(REF_STORAGE_KEY)
    } catch (_error) {
      // Nothing to clean up if storage is unreachable.
    }
    return null
  }
  return ref
}

/**
 * The per-tab session nonce, minted on first use. Random, never derived from
 * anything about the visitor.
 *
 * @returns The nonce, or `null` when sessionStorage is unavailable.
 */
function getSessionId(): string | null {
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY)
    if (existing) return existing
    const minted =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
    sessionStorage.setItem(SESSION_ID_KEY, minted)
    return minted
  } catch (_error) {
    // sessionStorage unreachable — the view is sent without a session nonce.
    return null
  }
}

/**
 * Reduce a referrer to origin + path — no query string (that is where other
 * sites put their own tokens). Mirrors the server's `normalizeReferrer`.
 *
 * @param referrer - `document.referrer`.
 * @returns The reduced referrer, or `null` when empty or not http(s).
 */
export function stripReferrer(referrer: string): string | null {
  if (!referrer) return null
  try {
    const url = new URL(referrer)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    return `${url.origin}${url.pathname}`
  } catch (_error) {
    // Not a parseable URL — no referrer to keep.
    return null
  }
}

/** Whether this tab already reported its external referrer. */
let referrerSent = false

/** The last path reported, so a same-path replace does not double count. */
let lastPath: string | null = null

/** The shape of a page-view request body. */
export interface PageViewBody {
  event: typeof PAGE_VIEW_EVENT
  metadata: { path: string; ref?: string; referrer?: string; sid?: string }
}

/**
 * Build the `page_view` body for a route, from what this tab knows.
 *
 * @param path - The route pathname.
 * @returns The request body.
 */
export function buildPageViewBody(path: string): PageViewBody {
  const ref = getStoredRef()
  const sid = getSessionId()
  const referrer = referrerSent ? null : stripReferrer(document.referrer)
  referrerSent = true
  return {
    event: PAGE_VIEW_EVENT,
    metadata: {
      path,
      ...(ref ? { ref } : {}),
      ...(referrer ? { referrer } : {}),
      ...(sid ? { sid } : {}),
    },
  }
}

/**
 * Report one page view to the API. Best-effort and silent: a failure (offline,
 * rate-limited, server down) must never reach the user.
 *
 * @param path - The route pathname.
 */
export async function sendPageView(path: string): Promise<void> {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return
  // Automation (the SSG prerender, Playwright runs) is not a visitor.
  if (navigator.webdriver) return
  if (path === lastPath) return
  lastPath = path
  try {
    await fetch(`${API_BASE_URL}/analytics/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify(buildPageViewBody(path)),
    })
  } catch (_error) {
    // Best-effort — never surface analytics failures.
  }
}

/**
 * Reset the per-tab reporting state. Test-only.
 */
export function resetPageViewStateForTests(): void {
  referrerSent = false
  lastPath = null
}

/** The first-party provider: page views only; everything else is a no-op. */
const firstPartyProvider: AnalyticsProvider = {
  async identify() {},
  async track() {},
  async page(pageView) {
    if (pageView.path) await sendPageView(pageView.path)
  },
}

/** Cleanup function returned by setupAutoTracking. */
let cleanupAutoTracking: (() => void) | undefined

/**
 * Wire the first-party analytics provider and the auto-tracking that feeds it
 * route changes; remember a `?ref=` the page loaded with; report the initial
 * view (the router only reports CHANGES).
 */
export function setupAppAnalytics(): void {
  setProvider(firstPartyProvider)

  // The HttpClient interface is a superset of auto-tracking's minimal
  // HttpClientLike, so the structural cast is safe.
  cleanupAutoTracking = setupAutoTracking({
    authClient,
    router: getRouter(),
    httpClient: getClient() as Parameters<typeof setupAutoTracking>[0]['httpClient'],
  })

  if (typeof window === 'undefined') return
  captureRef()
  void sendPageView(window.location.pathname)
}

/**
 * Tears down auto-tracking subscriptions (if needed on app unmount).
 */
export function teardownAppAnalytics(): void {
  cleanupAutoTracking?.()
  cleanupAutoTracking = undefined
}
